/**
 * Frontend phonics & spelling classifier.
 * Classifies (expected, attempted) letter pairs into confusion categories.
 *
 * Priority: spelling rules (word context) → digraph patterns (prevChar) → single-letter pairs.
 */

// ---------------------------------------------------------------------------
// Context interface
// ---------------------------------------------------------------------------

export interface LetterErrorContext {
  prevChar?: string;
  /** Full expected word containing the current letter (lowercase) */
  word: string;
  /** 0-based index of the current letter within the word */
  positionInWord: number;
}

// ---------------------------------------------------------------------------
// 1. Single-letter confusion categories (16 pairs)
// ---------------------------------------------------------------------------

const SINGLE_CATEGORIES: { key: string; pairs: [string, string][] }[] = [
  // Consonant voicing pairs
  { key: 'b_p', pairs: [['b','p'],['p','b']] },
  { key: 'd_t', pairs: [['d','t'],['t','d']] },
  { key: 'f_v', pairs: [['f','v'],['v','f']] },
  { key: 's_z', pairs: [['s','z'],['z','s']] },
  { key: 'k_g', pairs: [['k','g'],['g','k']] },
  // Consonant manner/place confusion
  { key: 'l_r', pairs: [['l','r'],['r','l']] },
  { key: 'v_w', pairs: [['v','w'],['w','v']] },
  { key: 'm_n', pairs: [['m','n'],['n','m']] },
  { key: 'b_v', pairs: [['b','v'],['v','b']] },
  { key: 'l_w', pairs: [['l','w'],['w','l']] },
  { key: 'b_g', pairs: [['b','g'],['g','b']] },
  // Same-sound spelling alternation
  { key: 'c_s', pairs: [['c','s'],['s','c']] },
  { key: 'c_k', pairs: [['c','k'],['k','c']] },
  { key: 'g_j', pairs: [['g','j'],['j','g']] },
  // Word-ending consonant confusion
  { key: 't_s', pairs: [['t','s'],['s','t']] },
  { key: 'd_s', pairs: [['d','s'],['s','d']] },
  { key: 't_k', pairs: [['t','k'],['k','t']] },
  // Vowel confusion
  { key: 'i_e', pairs: [['i','e'],['e','i']] },
  { key: 'a_e', pairs: [['a','e'],['e','a']] },
  { key: 'o_u', pairs: [['o','u'],['u','o']] },
  { key: 'a_u', pairs: [['a','u'],['u','a']] },
  { key: 'a_i', pairs: [['a','i'],['i','a']] },
  { key: 'a_o', pairs: [['a','o'],['o','a']] },
  { key: 'e_o', pairs: [['e','o'],['o','e']] },
  { key: 'i_o', pairs: [['i','o'],['o','i']] },
  { key: 'u_e', pairs: [['u','e'],['e','u']] },
  // Visual confusion
  { key: 'visual_bd', pairs: [['b','d'],['d','b']] },
  { key: 'visual_pq', pairs: [['p','q'],['q','p']] },
];

const PAIR_MAP = new Map<string, string>();
for (const cat of SINGLE_CATEGORIES) {
  for (const [a, b] of cat.pairs) {
    PAIR_MAP.set(`${a}_${b}`, cat.key);
  }
}

// ---------------------------------------------------------------------------
// 2. Digraph / spelling-pattern confusion (11 pairs)
// ---------------------------------------------------------------------------

interface DigraphRule {
  key: string;
  digraphs: string[];
}

const DIGRAPH_RULES: DigraphRule[] = [
  { key: 'ee_ea', digraphs: ['ee', 'ea'] },
  { key: 'oo_ou', digraphs: ['oo', 'ou'] },
  { key: 'ie_ei', digraphs: ['ie', 'ei'] },
  { key: 'ou_ow', digraphs: ['ou', 'ow'] },
  { key: 'ai_ay', digraphs: ['ai', 'ay'] },
  { key: 'oi_oy', digraphs: ['oi', 'oy'] },
  { key: 'er_ar', digraphs: ['er', 'ar'] },
  { key: 'er_or', digraphs: ['er', 'or'] },
  { key: 'tion_sion', digraphs: ['ti', 'si'] },
  { key: 'ph_f', digraphs: ['ph', 'pf'] },
  { key: 'ck_k', digraphs: ['ck', 'ik', 'ak', 'ek', 'ok', 'uk'] },
];

const DIGRAPH_MAP = new Map<string, string>();
for (const rule of DIGRAPH_RULES) {
  for (let i = 0; i < rule.digraphs.length; i++) {
    for (let j = 0; j < rule.digraphs.length; j++) {
      if (i !== j) {
        DIGRAPH_MAP.set(`${rule.digraphs[i]}_${rule.digraphs[j]}`, rule.key);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// 3. Word-level spelling rule detectors (5 new)
// ---------------------------------------------------------------------------

const CONSONANTS = 'bcdfghjklmnpqrstvwxyz';

/**
 * Consonant doubling before suffixes: run→running, stop→stopped.
 * The expected letter at `pos` is a repeated consonant right before a suffix.
 */
function checkDoubling(word: string, pos: number, expected: string): boolean {
  if (pos < 1 || !CONSONANTS.includes(expected)) return false;
  // Must be a doubled consonant (same as previous char)
  if (word[pos - 1] !== expected) return false;
  // Word must match: base + doubled consonant + suffix
  const re = /^(.+?)([bcdfgklmnprst])\2(ing|ed|er|est|en|y|able|ible|ish)$/;
  const m = word.match(re);
  if (!m) return false;
  // pos should be the second of the doubled consonants
  return pos === m[1].length + 1;
}

/**
 * Silent-e drop: make→making, hope→hoping.
 * User types 'e' where it was dropped before a vowel suffix.
 */
function checkSilentEDrop(word: string, pos: number, _expected: string, attempted: string): boolean {
  if (attempted !== 'e') return false;
  const VOWEL_SUFFIXES = ['ing', 'er', 'est', 'ed', 'able', 'ible', 'ous', 'ure', 'ation', 'ence', 'ance'];
  for (const suffix of VOWEL_SUFFIXES) {
    const suffixStart = word.length - suffix.length;
    if (suffixStart <= 1 || pos !== suffixStart) continue;
    if (word.slice(suffixStart) !== suffix) continue;
    // Base must end in a consonant (the consonant before the dropped e)
    const base = word.slice(0, suffixStart);
    if (base.length >= 2 && CONSONANTS.includes(base[base.length - 1])) return true;
  }
  return false;
}

/**
 * Y-to-I: happy→happiness, carry→carried.
 * User types 'y' where 'i' is expected.
 */
function checkYToI(word: string, pos: number, expected: string, attempted: string): boolean {
  if (expected !== 'i' || attempted !== 'y') return false;
  const re = /^(.+?)(i)(ed|es|er|est|ly|ness|ful|ment|ous|al|ance|ence)$/;
  const m = word.match(re);
  if (!m) return false;
  return pos === m[1].length;
}

/** Silent-letter patterns: position ranges that are silent */
interface SilentPattern {
  regex: RegExp;
  silentPositions: (word: string) => number[];
}

const SILENT_PATTERNS: SilentPattern[] = [
  // kn- at start: k is silent
  { regex: /^kn/, silentPositions: () => [0] },
  // wr- at start: w is silent
  { regex: /^wr/, silentPositions: () => [0] },
  // gn- at start: g is silent
  { regex: /^gn/, silentPositions: () => [0] },
  // ps- at start (psychology): p is silent
  { regex: /^ps/, silentPositions: () => [0] },
  // -mb at end: b is silent
  { regex: /mb$/, silentPositions: (w) => [w.length - 1] },
  // -mn at end (autumn, column): n is silent
  { regex: /mn$/, silentPositions: (w) => [w.length - 1] },
  // -ght: g and h are silent
  { regex: /ght/, silentPositions: (w) => { const i = w.indexOf('ght'); return [i, i + 1]; } },
  // wh- at start: h is silent (what, when, where)
  { regex: /^wh/, silentPositions: () => [1] },
];

/**
 * Silent letters: know, write, climb, night.
 * Error at a position that is a known silent letter.
 */
function checkSilentLetters(word: string, pos: number): boolean {
  for (const pattern of SILENT_PATTERNS) {
    if (!pattern.regex.test(word)) continue;
    if (pattern.silentPositions(word).includes(pos)) return true;
  }
  return false;
}

/**
 * Double consonant (non-suffix): accommodation, necessary, committee.
 * Position is a doubled consonant NOT at a suffix boundary.
 */
function checkDoubleConsonant(word: string, pos: number, expected: string): boolean {
  if (!CONSONANTS.includes(expected)) return false;
  // Check if this is part of a doubled consonant
  const isDoubled =
    (pos > 0 && word[pos - 1] === expected) ||
    (pos < word.length - 1 && word[pos + 1] === expected);
  if (!isDoubled) return false;
  // Exclude suffix-boundary doubles (already caught by checkDoubling)
  // If checkDoubling would match, we skip — but since checkDoubling runs first
  // in the priority chain, we only reach here if it didn't match.
  return true;
}

/**
 * British/American spelling alternation: -ise/-ize, -isation/-ization
 */
function checkBritishAmerican(word: string, pos: number, expected: string, attempted: string): boolean {
  if ((expected === 's' && attempted === 'z') || (expected === 'z' && attempted === 's')) {
    const suffixes = ['ise', 'ize', 'ised', 'ized', 'ises', 'izes', 'ising', 'izing',
      'isation', 'ization', 'isations', 'izations'];
    for (const suffix of suffixes) {
      const start = word.length - suffix.length;
      if (start < 1) continue;
      const tail = word.slice(start);
      const altTail = tail.replace(/s/g, 'z').replace(/z/g, 's');
      if ((tail === suffix || altTail === suffix) && pos >= start) return true;
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify a single-letter pair. Returns category key or null.
 */
export function classifyConfusion(expected: string, attempted: string): string | null {
  const key = `${expected.toLowerCase()}_${attempted.toLowerCase()}`;
  return PAIR_MAP.get(key) || null;
}

/**
 * Context-aware classification with 3-phase priority:
 *   1. Word-level spelling rules (if word context provided)
 *   2. Digraph patterns (if prevChar available)
 *   3. Single-letter pairs
 */
export function classifyWithContext(
  expected: string,
  attempted: string,
  context?: LetterErrorContext,
): { key: string; isDigraph: boolean } | null {
  const exp = expected.toLowerCase();
  const att = attempted.toLowerCase();

  // === Phase 1: Word-level spelling rules ===
  if (context?.word) {
    const word = context.word;
    const pos = context.positionInWord;

    if (checkBritishAmerican(word, pos, exp, att)) {
      return { key: 'british_american', isDigraph: true };
    }
    if (checkDoubling(word, pos, exp)) {
      return { key: 'doubling', isDigraph: true };
    }
    if (checkSilentEDrop(word, pos, exp, att)) {
      return { key: 'silent_e_drop', isDigraph: true };
    }
    if (checkYToI(word, pos, exp, att)) {
      return { key: 'y_to_i', isDigraph: true };
    }
    if (checkSilentLetters(word, pos)) {
      return { key: 'silent_letters', isDigraph: true };
    }
    if (checkDoubleConsonant(word, pos, exp)) {
      return { key: 'double_consonant', isDigraph: true };
    }
  }

  // === Phase 2: Digraph patterns ===
  const prev = context?.prevChar?.toLowerCase();
  if (prev) {
    const expectedDigraph = prev + exp;
    const attemptedDigraph = prev + att;
    const digraphKey = DIGRAPH_MAP.get(`${expectedDigraph}_${attemptedDigraph}`);
    if (digraphKey) {
      return { key: digraphKey, isDigraph: true };
    }
  }

  // === Phase 3: Single-letter pairs ===
  const singleKey = classifyConfusion(exp, att);
  if (singleKey) {
    return { key: singleKey, isDigraph: false };
  }

  return null;
}
